/**
 * @file Calendar-domain date normalization and month-key helpers.
 */

/**
 * Returns a stable YYYY-MM month key for a Date-like value.
 * @param {Date|string|number} value Date-like value.
 * @returns {string|null} Month key, or null when invalid.
 */
export function getCalendarMonthKey(value) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
}

/**
 * Returns the current local Calendar month key.
 * @returns {string} Current month key.
 */
export function getCurrentCalendarMonthKey() {
    return getCalendarMonthKey(new Date());
}

/**
 * Parses a YYYY-MM month key into a local Date at the first of the month.
 * @param {string} monthKey Month key.
 * @returns {Date|null} Month date, or null.
 */
export function parseCalendarMonthKey(monthKey) {
    const match = String(monthKey || "").match(/^(\d{4})-(\d{2})$/);
    if (!match) {return null;}

    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const date = new Date(year, monthIndex, 1);
    return date.getFullYear() === year && date.getMonth() === monthIndex ?
        date :
        null;
}

/**
 * Adds months to a YYYY-MM month key.
 * @param {string} monthKey Month key.
 * @param {number} offset Month offset.
 * @returns {string|null} Result month key.
 */
export function addCalendarMonths(monthKey, offset) {
    const date = parseCalendarMonthKey(monthKey);
    if (!date) {return null;}
    date.setMonth(date.getMonth() + offset);
    return getCalendarMonthKey(date);
}

/**
 * Returns a sequential list of month keys around the visible Calendar range.
 * @param {string} visibleStartMonth Visible start month key.
 * @param {number} visibleMonthCount Number of visible months.
 * @param {number} bufferMonths Number of buffer months on either side.
 * @returns {Array<string>} Month keys.
 */
export function getBufferedCalendarMonthKeys(
    visibleStartMonth,
    visibleMonthCount = 2,
    bufferMonths = 3,
) {
    const keys = [];
    for (let offset = -bufferMonths;
        offset < visibleMonthCount + bufferMonths;
        offset += 1) {
        const monthKey = addCalendarMonths(visibleStartMonth, offset);
        if (monthKey) {keys.push(monthKey);}
    }
    return keys;
}

/**
 * Formats a month key for Calendar headings.
 * @param {string} monthKey Month key.
 * @returns {string} Display label.
 */
export function formatCalendarMonthLabel(monthKey) {
    const date = parseCalendarMonthKey(monthKey);
    if (!date) {return monthKey || "";}
    return new Intl.DateTimeFormat(undefined, {
        month: "long",
        year: "numeric",
    }).format(date);
}

/**
 * Returns YYYY-MM-DD for a local date.
 * @param {Date} date Local date.
 * @returns {string} Date key.
 */
export function getCalendarDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/**
 * Builds a six-week month grid for stable Calendar layout.
 * @param {string} monthKey Month key.
 * @returns {Array<object>} Calendar day cells.
 */
export function buildCalendarMonthDays(monthKey) {
    const monthDate = parseCalendarMonthKey(monthKey);
    if (!monthDate) {return [];}

    const firstDay = new Date(monthDate);
    const gridStart = new Date(firstDay);
    const mondayOffset = (firstDay.getDay() + 6) % 7;
    gridStart.setDate(firstDay.getDate() - mondayOffset);

    return Array.from({ length: 42 }, (_, index) => {
        const date = new Date(gridStart);
        date.setDate(gridStart.getDate() + index);
        return {
            date,
            dateKey: getCalendarDateKey(date),
            dayNumber: date.getDate(),
            isCurrentMonth: date.getMonth() === monthDate.getMonth(),
            weekday: date.getDay(),
        };
    });
}

/**
 * Returns whether a calendar entry intersects a date key.
 * @param {object} entry Display-safe calendar entry.
 * @param {string} dateKey Date key.
 * @returns {boolean} True when the entry spans the date.
 */
export function entryOccursOnDate(entry, dateKey) {
    const startDate = entry?.startDate || "";
    const endDate = entry?.endDate || startDate;
    return Boolean(startDate && dateKey >= startDate && dateKey <= endDate);
}
